import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cannesEventPriceFaq, cannesEventSeo } from "../slugs/eventFaq";

const cannesLions: ChauffeurPage = {
  slug: "cannes-lions",
  title: "Private chauffeur Cannes Lions – Agency VTC",
  subtitle:
    "Private chauffeur during Cannes Lions: agency-team shuttles, beach-club drop-offs and yacht quays around the Palais des Festivals.",
  keywords: [
    "cannes lions chauffeur",
    "cannes lions private chauffeur",
    "agency chauffeur cannes lions",
  ],
  hero: {
    title: "Private chauffeur during Cannes Lions",
    subtitle:
      "The Festival of Creativity, in June: a chauffeur for agency teams between the Palais, beach clubs and yachts.",
    keywords: [
      "Cannes Lions chauffeur",
      "agency VTC Cannes",
      "Croisette beach-club chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during Cannes Lions",
    badges: ["Agency-team shuttles", "Beach-club drop-offs", "Yacht quays"],
  },
  benefits: {
    heading: "Why a chauffeur during Cannes Lions",
    items: [
      {
        title: "Packed creative agenda",
        description:
          "Palais talks, masterclasses, brand activations and award ceremonies: a dedicated chauffeur so you miss nothing.",
      },
      {
        title: "Beach clubs & yachts",
        description:
          "Platform-hosted private beaches and yachts moored at the Old Port: drop-offs as close as possible to the activations.",
      },
      {
        title: "Teams & talent",
        description:
          "Shuttles for agency staff, clients and speakers, with separate flows as needed.",
      },
      {
        title: "Late nights",
        description:
          "Brand parties and after-parties on the Croisette until dawn: a guaranteed return with a chauffeur who waits.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during Cannes Lions",
    services: [
      {
        title: "Nice airport transfer",
        text: "Nice ↔ Cannes link with flight tracking during the festival influx in June.",
      },
      {
        title: "Agency-team shuttles",
        text: "Multi-vehicle coordination for staff, clients and delegations between hotels and the Palais.",
      },
      {
        title: "Beach-club drop-offs",
        text: "Approach as close as possible to the private beaches and brand activations on the Croisette.",
      },
      {
        title: "Yacht quays",
        text: "Drop-off and pick-up at the Old Port of Cannes for yacht parties and business meetings.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur by the hour or day to chain talks, activations and evenings.",
      },
    ],
  },
  locations: {
    heading: "Key points during Cannes Lions",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description: "Talks, masterclasses and the Lions ceremony.",
      },
      {
        name: "Croisette beaches",
        description: "Beach clubs hosted by the major platforms.",
      },
      {
        name: "Old Port of Cannes",
        description: "Yacht quays privatised for parties and meetings.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, creative influx concentrated in June.",
      },
      {
        name: "La Croisette",
        description: "Palaces, terraces and brand activations.",
      },
    ],
  },
  generic: {
    heading: "Cannes Lions from a chauffeur's seat",
    subheading:
      "In June, the Festival of Creativity spreads Cannes from the Palais to the beach clubs and the Old Port yachts.",
    paragraphs: [
      "Every June, the Cannes Lions International Festival of Creativity gathers agencies, brands, media and tech at the Palais des Festivals. But much of it also plays out on the private beaches of the Croisette and on the yachts moored at the Old Port, where talks and activations chain at a brisk pace.",
      "Agency teams and their clients arrive in numbers at Nice Côte d'Azur airport. Flight tracking, a name-board welcome and coordinated shuttles smooth the first and last days of the festival, often the busiest.",
      "Hourly hire keeps the same chauffeur across the day and evening: beach-club drop-offs, yacht quays, dinners and brand parties until the small hours, without rebooking for each trip.",
    ],
    bullets: [
      "Beach-club drop-offs",
      "Old Port yacht quays",
      "After-parties until dawn",
    ],
  },
  process: {
    heading: "Booking a chauffeur for Cannes Lions",
    steps: [
      "Tell us your dates, your hotels and your activation venues (Palais, beaches, yachts).",
      "We coordinate the shuttles and drop-offs as close as possible to the beach clubs and quays.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Day and evening hire for talks, activations and parties.",
    ],
  },
  faq: [
    ...cannesEventPriceFaq("en", "during Cannes Lions"),
    {
      q: "How do I book a chauffeur during Cannes Lions?",
      a: "Book early: in mid-June, Cannes is full and chauffeur demand is high. Send your dates, your hotels and your activation venues so we can secure a dedicated chauffeur and coordinate the shuttles.",
    },
    {
      q: "Can you drop off at the beach clubs and yacht quays?",
      a: "Yes. During the festival, many Croisette beaches and Old Port yachts are privatised by brands. The chauffeur drops you as close as possible to the activation and collects you on the way out.",
    },
    {
      q: "Do you handle shuttles for a full agency team?",
      a: "Yes, we coordinate multi-vehicle fleets for staff, clients and speakers, with separate flows between hotels, the Palais and evening venues.",
    },
    {
      q: "Do you cover returns after the brand parties?",
      a: "Yes. Cannes Lions after-parties run late: the chauffeur waits and guarantees your late-night return to the hotel.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur Cannes to Nice Airport",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Chauffeur Cannes to Saint-Tropez",
      href: "/en/chauffeur/transfers/cannes-saint-tropez",
    },
    {
      label: "Get a quote",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for Cannes Lions",
    text: "Agency shuttles, beach clubs and yachts: book a dedicated chauffeur for the festival.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : page événement sans prix, même recette que le Festival.
    ...cannesEventSeo("en", "Cannes Lions", "Beach clubs and yacht quays."),
    canonical: "/en/chauffeur/cannes-lions",
  },
};

export default cannesLions;
